import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteExam, deleteExamC, deleteExamD, fetchExams } from "../api/client";
import type { ExamSummary } from "../api/types";
import { Plus, Trash2, FileText } from "lucide-react";
import { useOrgContext } from "../state/OrgContext";
import { useTranslation } from "react-i18next";

type State =
  | { status: "loading" }
  | { status: "ready"; sessions: ExamSummary[]; totalCost: number }
  | { status: "error"; message: string };

const SessionsPage = () => {
  const [state, setState] = useState<State>({ status: "loading" });
  const navigate = useNavigate();
  const { activeRole } = useOrgContext();
  const { t, i18n } = useTranslation("pages");

  const loadSessions = () => {
    setState({ status: "loading" });
    fetchExams()
      .then((data) =>
        setState({
          status: "ready",
          sessions: data.exams || [],
          totalCost: data.total_cost || 0,
        })
      )
      .catch((err) =>
        setState({
          status: "error",
          message: err.message || t("sessions.loadError"),
        })
      );
  };

  useEffect(() => {
    loadSessions();
  }, []);

  const onDelete = async (e: React.MouseEvent, exam: ExamSummary) => {
    e.stopPropagation();
    if (!confirm(t("sessions.deleteConfirm"))) return;
    try {
      if (exam.grading_mode === "mod_c") await deleteExamC(exam.id);
      else if (exam.grading_mode === "mod_d") await deleteExamD(exam.id);
      else await deleteExam(exam.id);
      loadSessions();
    } catch (err) {
      alert(err instanceof Error ? err.message : t("sessions.deleteError"));
    }
  };

  const examPath = (exam: ExamSummary): string => {
    if (exam.grading_mode === "mod_c") return `/exams-c/${exam.id}`;
    if (exam.grading_mode === "mod_d") return `/exams-d/${exam.id}`;
    return `/exams/${exam.id}`;
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-end mb-24">
        {activeRole !== "UNKNOWN" && (
          <button
            className="btn btn-primary"
            onClick={() => navigate("/exams/new")}
          >
            <Plus size={16} />
            {t("sessions.newExam")}
          </button>
        )}
      </div>

      {/* Loading */}
      {state.status === "loading" && (
        <div className="card card-padding">
          <div className="empty-state">
            <p className="text-muted">{t("sessions.loading")}</p>
          </div>
        </div>
      )}

      {/* Error */}
      {state.status === "error" && (
        <div className="card card-padding">
          <div className="empty-state">
            <p style={{ color: "var(--color-danger)" }}>{state.message}</p>
          </div>
        </div>
      )}

      {/* Empty state */}
      {state.status === "ready" && state.sessions.length === 0 && (
        <div className="card card-padding">
          <div className="empty-state">
            <FileText />
            <h3>{t("sessions.emptyHeading")}</h3>
            <p>{t("sessions.emptyBody")}</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate("/exams/new")}
            >
              <Plus size={16} />
              {t("sessions.emptyButton")}
            </button>
          </div>
        </div>
      )}

      {/* Exam cards grid */}
      {state.status === "ready" && state.sessions.length > 0 && (
        <div className="grid-4">
          {state.sessions.map((exam) => (
            <div
              key={exam.id}
              className="card card-padding card-hoverable exam-card"
              onClick={() => navigate(examPath(exam))}
            >
              {activeRole !== "UNKNOWN" && (
                <button
                  className="exam-card-delete"
                  onClick={(e) => onDelete(e, exam)}
                  title={t("sessions.deleteTitle")}
                >
                  <Trash2 size={16} />
                </button>
              )}

              <div className="exam-card-header">
                <span className="text-xs text-muted">
                  {formatDate(exam.test_date)}
                </span>
              </div>

              <div className="exam-card-title">{exam.session_name}</div>

              <div className="exam-card-stats">
                <div className="exam-card-stat">
                  <div className="exam-card-stat-value">
                    {formatDate(exam.test_date)}
                  </div>
                  <div className="exam-card-stat-label">{t("sessions.dateStat")}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SessionsPage;
